'use memo';

import { useCallback, useContext } from 'react';
import {
  AgxPropertyAddressForm,
  AustralianState,
  PropertyAddress,
} from '@urbanx/agx-ui-components';
import {
  DevCampaignScenarioContractStage,
  DevCampaignScenarioCreateFrom,
  DevCampaignScenarioCreateValues,
  DevCampaignScenarioCreatedCampaign,
  DevCampaignScenarioDialog,
  DevCampaignScenarioPropertyAddress,
  DevCampaignScenarioState,
} from '@urbanx/agx.ui-components2';
import { AuthContext } from '@urbanx/agx.auth.client';
import { progressDevContractWorkflow } from 'Api/Activities/activitiesApi';
import { createDevContractTestCampaign } from 'Api/Campaigns/campaignsApi';
import {
  DevAgreementSource,
  DevContractConditions,
  DevContractCreationSource,
  DevContractSeedMode,
  DevContractSeedSource,
  DevContractStage,
  DevDepositProfile,
  DevListingLaunch,
} from 'Api/Campaigns/Types/types';
import { fetchAddresses, fetchPropertyDetails } from 'Api/RpData/rpDataApi';
import { PropertySuggestionItem } from 'Api/RpData/Types/types';
import {
  reloadCampaigns,
  selectCampaign,
} from 'features/campaigns/campaignsReducer';
import { useAppDispatch } from 'hooks/useAppDispatch';
import useSuburbSuggestions from 'hooks/useSuburbSuggestions';

interface DevCampaignScenarioModalProps {
  onClose: () => void;
}

interface ManualAddressInputProps {
  state: DevCampaignScenarioState;
  disabled: boolean;
  value: DevCampaignScenarioPropertyAddress;
  onChange: (value: DevCampaignScenarioPropertyAddress) => void;
  onValidityChange: (isValid: boolean) => void;
}

const toAustralianState = (state: DevCampaignScenarioState) =>
  state as AustralianState;

const getStaffPortalCampaignUrl = ({
  campaignId,
  agencyId,
}: DevCampaignScenarioCreatedCampaign) => {
  const staffPortalOrigin = ['localhost', '127.0.0.1'].includes(
    window.location.hostname
  )
    ? `${window.location.protocol}//${window.location.hostname}:3001`
    : window.location.origin;

  return `${staffPortalOrigin}/agencies/${agencyId}/campaigns/${campaignId}/campaign-tasks`;
};

const ManualAddressInput = ({
  state,
  disabled,
  value,
  onChange,
  onValidityChange,
}: ManualAddressInputProps) => {
  const { suburbSuggestions, getSuburbSuggestions } = useSuburbSuggestions(
    toAustralianState(state)
  );

  return (
    <AgxPropertyAddressForm
      key={`dev-campaign-scenario-manual-address-${state}`}
      id="dev-campaign-scenario-manual-address"
      searchSuburb
      hideUnitLot
      showSearchForAddress={false}
      propertyAddressField={false}
      suburbSuggestions={suburbSuggestions}
      stateToSearch={toAustralianState(state)}
      onFetchSuburbSuggestions={suburbTerm =>
        getSuburbSuggestions(suburbTerm || '')
      }
      onClearSuburbSuggestions={() => getSuburbSuggestions('')}
      onDisableNextButton={disabled => onValidityChange(!disabled)}
      disabled={disabled}
      required
      defaultValue={value as PropertyAddress}
      onAddressSelected={selectedAddress => {
        if (selectedAddress) onChange(selectedAddress);
      }}
    />
  );
};

export const DevCampaignScenarioModal = ({
  onClose,
}: DevCampaignScenarioModalProps) => {
  const { getAuthToken } = useContext(AuthContext);
  const dispatch = useAppDispatch();

  const getRequiredAuthToken = useCallback(async () => {
    const authToken = await getAuthToken?.();
    if (!authToken) throw new Error('Unable to authenticate dev seeder call.');
    return authToken;
  }, [getAuthToken]);

  const selectFirstSuggestion = useCallback(
    async (
      authToken: string,
      query: string,
      state: DevCampaignScenarioState
    ): Promise<PropertySuggestionItem | null> => {
      const abortController = new AbortController();
      const response = await fetchAddresses(
        authToken,
        query,
        toAustralianState(state),
        abortController.signal
      );

      return response?.suggestions?.[0] ?? null;
    },
    []
  );

  const createFromRpData = useCallback(
    async (authToken: string, values: DevCampaignScenarioCreateValues) => {
      const suggestion = await selectFirstSuggestion(
        authToken,
        values.rpDataQuery,
        values.state
      );
      const propertyId = suggestion?.suggestionProperties?.[0]?.propertyId;

      if (!propertyId)
        throw new Error('No RPData property was found for that search.');

      const allPropertyDetails = await fetchPropertyDetails(
        authToken,
        propertyId
      );

      if (!allPropertyDetails)
        throw new Error('RPData did not return property details.');

      return createDevContractTestCampaign(authToken, {
        createFrom: DevContractSeedSource.RPDataSearch,
        state: toAustralianState(values.state),
        propertyAddress: allPropertyDetails.matchedAddress,
        propertyDetails: allPropertyDetails.propertyDetails,
        propertyImageUrls: allPropertyDetails.propertyImageUrls,
        vendors: allPropertyDetails.vendors,
        hasPool: allPropertyDetails.hasPool,
        agreementSource: values.agreementSource as unknown as DevAgreementSource,
        agreementDocuSignSigned: values.agreementDocuSignSigned,
        listingLaunch: values.listingLaunch as unknown as DevListingLaunch,
        contract: values.contract as unknown as DevContractSeedMode,
        contractCreationSource: DevContractCreationSource.AgentPortal,
        contractConditions:
          values.conditions as unknown as DevContractConditions,
        contractStage: values.contractStage as unknown as DevContractStage,
        depositProfile: values.depositProfile as unknown as DevDepositProfile,
      });
    },
    [selectFirstSuggestion]
  );

  const handleCreate = useCallback(
    async (values: DevCampaignScenarioCreateValues) => {
      const authToken = await getRequiredAuthToken();

      if (values.createFrom === DevCampaignScenarioCreateFrom.RPDataSearch)
        return createFromRpData(authToken, values);

      return createDevContractTestCampaign(authToken, {
        createFrom: DevContractSeedSource.ManualAddress,
        state: toAustralianState(values.state),
        propertyAddress: {
          ...values.manualAddress,
          state: values.manualAddress.state || values.state,
        } as PropertyAddress,
        agreementSource: values.agreementSource as unknown as DevAgreementSource,
        agreementDocuSignSigned: values.agreementDocuSignSigned,
        listingLaunch: values.listingLaunch as unknown as DevListingLaunch,
        contract: values.contract as unknown as DevContractSeedMode,
        contractCreationSource: DevContractCreationSource.AgentPortal,
        contractConditions:
          values.conditions as unknown as DevContractConditions,
        contractStage: values.contractStage as unknown as DevContractStage,
        depositProfile: values.depositProfile as unknown as DevDepositProfile,
      });
    },
    [createFromRpData, getRequiredAuthToken]
  );

  const handleProgressContractWorkflow = useCallback(
    async (
      campaign: DevCampaignScenarioCreatedCampaign,
      status: DevCampaignScenarioContractStage
    ) => {
      if (!campaign.contractId) return;

      const authToken = await getRequiredAuthToken();
      await progressDevContractWorkflow(authToken, {
        campaignId: campaign.campaignId,
        contractId: campaign.contractId,
        status: status as unknown as DevContractStage,
      });
    },
    [getRequiredAuthToken]
  );

  const handleCreated = useCallback(
    (campaign: DevCampaignScenarioCreatedCampaign) => {
      dispatch(reloadCampaigns());
      dispatch(selectCampaign(campaign.campaignId));
    },
    [dispatch]
  );

  return (
    <DevCampaignScenarioDialog
      onClose={onClose}
      onCreate={handleCreate}
      onProgressContractWorkflow={handleProgressContractWorkflow}
      onCreated={handleCreated}
      getStaffPortalUrl={getStaffPortalCampaignUrl}
      renderManualAddressForm={props => <ManualAddressInput {...props} />}
    />
  );
};

export default DevCampaignScenarioModal;
